import { useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { controlChrome, editorBehavior } from '../editorChrome'
import { Button } from '../Button'
import { OptionBullet, OptionLabel } from '../OptionLabel'
import { GroupedChips } from '../GroupedChips'
import { serverGroups, sortGroups } from '../valueGroups'
import { offeredOptions } from '../optionColor'

interface Option {
  id: string
}

/** E5 · select content — coloured bullet + label. */
function EnumContent({ value, config }: CellContentProps) {
  if (value == null || value === '') {
    return <span className="text-muted-foreground">—</span>
  }
  // SEN-205: a SET of options (a path column ending in a select) — the
  // server's groups carry exact counts per option key ("retail ×123").
  const fromServer = serverGroups(value)
  if (fromServer) {
    const groups = sortGroups(fromServer.map((g) => ({
      key: String(g.value), label: String(g.value), sample: String(g.value), count: g.count, samples: g.samples,
    })))
    return (
      <GroupedChips
        groups={groups}
        renderGroup={(group, badge, compact) => (
          <>
            {compact
              ? <OptionBullet value={group.key} options={config?.options} />
              : null}
            <span data-testid="set-group-label" className={compact ? 'sr-only' : 'inline-flex min-w-0 items-center truncate'}>
              {compact ? group.key : <OptionLabel value={group.key} options={config?.options} />}
            </span>
            {badge}
          </>
        )}
      />
    )
  }
  return <OptionLabel value={String(value)} options={config?.options} />
}

const Chevron = () => (
  <svg viewBox="0 0 24 24" className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
)
const Check = () => (
  <svg viewBox="0 0 24 24" className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 13l4 4L19 7" />
  </svg>
)
const Search = () => (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
)

/**
 * select editor — Attio-style custom dropdown (not a native <select>, which
 * drops the bullet and resizes the cell). The trigger mirrors the read content
 * (bullet + label) so width stays stable and the bullet is preserved; a floating
 * popover lists the options with a search-or-create input.
 *
 * The options are config.options: the attribute read serves them, a
 * server-resolved vocabulary included (config.options_resolver stays named —
 * that vocabulary is the server's, so it offers no inline create).
 */
function EnumEditor({ value, onCommit, onCancel, config, context }: CellEditorProps) {
  const options: Option[] = (config?.options as Option[] | undefined) ?? []
  const serverOwned = config?.options_resolver != null
  const current = value == null ? '' : String(value)
  const [query, setQuery] = useState('')
  // form context: controlled open state (closed on mount, commit closes)
  // Declared per-context capabilities — no inline which-context-am-I tests.
  const b = editorBehavior(context)
  const [open, setOpen] = useState(false)

  const q = query.trim().toLowerCase()
  // a retired option is never offered (its records still show it: the trigger
  // draws the current value from the full list)
  const offered = offeredOptions(options)
  const filtered = q ? offered.filter((o) => o.id.toLowerCase().includes(q)) : offered
  const exact = options.some((o) => o.id.toLowerCase() === q)
  // Only a static, attribute-owned vocabulary may grow through the inline
  // create affordance.
  const showCreate = !serverOwned && context !== 'filter' && q !== '' && !exact

  function handleCommit(next: unknown) {
    if (b.controlledOpen) setOpen(false)
    onCommit(next)
  }

  return (
    <Popover.Root
      open={b.controlledOpen ? open : undefined}
      defaultOpen={!b.controlledOpen}
      onOpenChange={(o) => {
        if (b.controlledOpen) setOpen(o)
        if (!o && b.dismissCancels) onCancel()
      }}
    >
      <Popover.Trigger asChild>
        <button
          data-testid="cell-editor"
          className={`flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-sm text-foreground outline-none ${controlChrome(context)}`}
          onClick={b.controlledOpen ? () => setOpen(true) : undefined}
        >
          {current
            ? <OptionLabel value={current} options={options} />
            : <span className="text-muted-foreground">—</span>}
          <Chevron />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-[max(var(--radix-popover-trigger-width),12rem)] overflow-hidden rounded-float border border-border bg-background shadow-float"
        >
          <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
            <Search />
            <input
              autoFocus
              data-testid="cell-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  if (b.controlledOpen) setOpen(false)
                  else onCancel()
                } else if (e.key === 'Enter') {
                  e.preventDefault()
                  if (showCreate) handleCommit(query.trim())
                  else if (filtered[0]) handleCommit(filtered[0].id)
                }
              }}
              placeholder={context === 'filter' || serverOwned ? 'Search…' : 'Search or create…'}
              className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-60 overflow-auto p-1">
            {filtered.map((o) => (
              <Button variant="menuItem" fullWidth
                key={o.id}
                onClick={() => handleCommit(o.id)}
                className="flex items-center gap-1.5"
              >
                {/* The attribute's list, as the read cell sees it. */}
                <OptionLabel value={o.id} options={options} />
                {o.id === current && <Check />}
              </Button>
            ))}
            {showCreate && (
              <Button variant="menuItem" fullWidth
                data-testid="cell-create-option"
                onClick={() => handleCommit(query.trim())}
                className="flex items-center gap-1.5 text-muted-foreground"
              >
                <span className="text-base leading-none">+</span>
                <span className="truncate">
                  Create <span className="font-medium text-foreground">"{query.trim()}"</span>
                </span>
              </Button>
            )}
            {filtered.length === 0 && !showCreate && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">No options</div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

registerWidget('select', { read: EnumContent, write: EnumEditor, defaultWidth: 140, readsSets: true })
